/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 목록 블록에서 쓰이는 아이템 블록을 정의한다.
 *
 * @file /src/rootin/blocks/list/blocks/Item.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Text } from '@rootin/elements/text/Text';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './Item.css';

export class Item extends Block {
    /**
     * 블록타입
     */
    static type = 'list-item';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'inline';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-icon lucide-list"><path d="M3 12h.01"/><path d="M3 18h.01"/><path d="M3 6h.01"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M8 6h13"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.list.label');

    /**
     * 텍스트 엘리먼트
     */
    text: Text;

    /**
     * 목록 블록에서 쓰이는 아이템 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
        this.$getBlock().append(Html.create('i', { 'data-role': 'bullet', 'contenteditable': 'false' }));

        this.text = new Text(this, {
            name: 'text',
            placeholder: Language.get('rootin.blocks.list.item.placeholder'),
        });
        this.$getBlock().append(this.text.$getElement());
    }

    /**
     * 블록에 포커스를 지정한다.
     *
     * @param to_start - 포커스 위치 (TRUE : 처음, FALSE : 끝, NULL : 앵커)
     */
    focus(to_start: boolean | null): void {
        if (to_start === null) {
            return;
        }
        this.text.focus(to_start);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.text.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.text.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return { id: this.getId(), type: this.getType(), text: this.text.getValue() };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.text.setValue(value.text);
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        const $text = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'text'
        );
        if ($text !== undefined) {
            this.text.$getElement().html($text.innerHTML);
        }
    }

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return { texts: [], remain: null };
        }

        const $el = this.text.$getElement().getEl();
        if (range.intersectsNode($el) === false) {
            return { texts: [], remain: null };
        }

        // 아이템은 부분추출 없이 is_all 값과 무관하게 항상 전체를 통째로 넘긴다.
        const text = this.text.getValue() as string;

        this.text.expectEdit();
        this.text.$getElement().html('');
        this.text.checkSynced();

        if (this.isEmpty() === true) {
            await this.getParent()?.removeBlock(this);
        }

        return { texts: [text], remain: null };
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        this.text.focus();
    }

    /**
     * 블록이 포커스를 받았을 때 이벤트를 처리한다.
     *
     * @param element - 캐럿이 위치한 엘리먼트 (NULL 이면 아직 캐럿 위치가 없다는 뜻)
     */
    onFocus(element: Element | null): void {
        if (element === null) {
            this.text.focus();
        }
    }

    /**
     * 블록이 포커스를 받을 가능성이 있을 때 이벤트를 처리한다.
     */
    onFocusable(): void {
        this.onFocus(null);
    }

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element): void {
        if (this.isEmpty() === true) {
            this.#exitList();
            return;
        }

        this.getParent().splitBlock(new Item(this.editor), this, element);
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (type.startsWith('text/') === false) {
            return data;
        }

        const wrappers = [...Text.genericWrapperTags, `rootin-block[data-type='${this.getType()}']`];

        if (focused instanceof Element) {
            return (await this.text.onPaste(type, data, wrappers)).remain;
        }

        if (focused === null) {
            return data;
        }

        const container = focused.getParent();
        if (container === null) {
            return data;
        }

        const { remain } = await this.text.onPaste(type, data, wrappers);
        if (this.text.isEmpty() === false) {
            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
        }

        return remain;
    }

    /**
     * 빈 아이템에서 Enter 를 눌러 목록을 빠져나온다.
     */
    #exitList(): void {
        const itemsContainer = this.getParent();
        const list = itemsContainer?.block ?? null;
        const outerContainer = list?.getParent() ?? null;
        if (itemsContainer === null || list === null || outerContainer === null) {
            return;
        }

        if (outerContainer.blocks.includes('paragraph') === false) {
            return;
        }

        const ParagraphClass = this.getBlockClass('paragraph');
        if (ParagraphClass === null) {
            return;
        }

        const before = list.$getBlock().getEl().nextSibling;
        (async () => {
            await itemsContainer.removeBlock(this);
            await outerContainer.insertBlock(ParagraphClass.create(this.editor), before);
        })();
    }
}
